import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, Checkbox, Chip, LinearProgress } from "@material-ui/core";
import * as _ from 'lodash';

import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TablePagination from "@material-ui/core/TablePagination";
import { Link, useHistory } from "react-router-dom";

import AppUtil from "utilities/AppUtil";
import AppButton from "components/CustomButtons/AppButton";
import AppColor from "utilities/AppColor";
import InputSearch from "components/Input/InputSearch";
import AppConfirmDialog from "components/Dialog/AppConfirmDialog";
import AppTable from "views/Admin/Component/Table/AppTable";
import TablePaginationActions from "views/Admin/Component/Table/TablePaginationActions";
import {
  fetchListJob,
  getJobList,
  reduceDeleteJob
} from "store/slices/job";
import { JOB_STATUS, SYSTEM_ROLE } from "config/AppConfig";
import { getAuthInfo } from "store/slices/auth";
import { fetchJobPageForCompany } from "store/slices/job";

const JobTable = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const [queries, setQueries] = useState({
    sort: true,
    page_number: 1,
    page_size: 10,
    search_key: "",
    sort_field: "",
  });
  const clientList = useSelector(getJobList);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selected, setSelected] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isOnSelectedAll, setIsOnSelectedAll] = useState(false);

  const [searchKey, setSearchKey] = useState("");

  const emptyRows = data.length === 0;
  const authInfo = useSelector(getAuthInfo) || {};

  const { role } = authInfo;
  let isHospital = false;
  let isCoordinator = false;

  switch (role) {
    case SYSTEM_ROLE.HOSPITAL_ADMIN:
    case SYSTEM_ROLE.HOSPITAL_OTHER:
      isHospital = true;
      break;
    case SYSTEM_ROLE.COORDINATOR_ADMIN:
    case SYSTEM_ROLE.COORDINATOR_OTHER:
      isCoordinator = true;
      break;
    default:
      break;
  }

  const headCells = [
    {
      id: "title",
      numeric: "left",
      disablePadding: false,
      label: "label.job_title",
      sort: true,
    },
    {
      id: "participant_name",
      numeric: "left",
      disablePadding: false,
      label: "label.participant_name",
      sort: false,
    },
    {
      id: isHospital ? "specialty" : "support",
      numeric: "left",
      disablePadding: false,
      label: isHospital ? "label.specialty" : "label.support",
      sort: false,
    },
    {
      id: "expiredTime",
      numeric: "left",
      disablePadding: false,
      label: "label.expiredTime",
      sort: false,
    },
    {
      id: "status",
      numeric: "left",
      disablePadding: false,
      label: "label.status",
      sort: false,
    },
    {
      id: "actions",
      numeric: "center",
      disablePadding: false,
      label: "label.actions",
      sort: false,
    },
  ];

  const _handleDeleteClients = async () => {
    if (selected.length > 0) {
      const selectedFilter = selected.filter(id => {
        if (typeof id !== 'boolean'){
          return id;
        }
      });
      await dispatch(reduceDeleteJob({ ids: selectedFilter }));
      setShowDeleteModal(false);
      setSelected([]);
      if(data.length === 1 || selectedFilter.length === data.length) {
        _loadData({ ...queries, page_number: queries.page_number -1});
      } else {
        if (queries.search_key) {
          _loadData({ ...queries, search_key: queries.search_key });
        } else {
          _loadData();
        }
      }
    }
  }

  const _handleSelect = (id) => {
    if (!selected.includes(id)) {
      setSelected([...selected, id]);
    } else {
      setSelected(selected.filter(item => item !== id))
    }
  }

  const _handleSelectAll = () => {
    const dataCheck = _.union(dataCheck, data.map(item => item.isAllowDelete && item.id));
    if (dataCheck.length === selected.length) {
      setSelected([]);
    } else {
      setSelected(_.union(selected, data.map(item => item.isAllowDelete && item.id)));
    }
  }

  const handleChangePage = (event, newPage) => {
    _loadData({ ...queries, page_number: newPage + 1 });
    AppUtil.scrollToTopById("main-panel");
  };

  const handleChangeRowsPerPage = (event) => {
    _loadData({
      ...queries,
      page_number: 1,
      page_size: parseInt(event.target.value),
    });
    AppUtil.scrollToTopById("main-panel");
  };

  const onRequestSort = (event, property) => {
    _loadData({
      ...queries,
      sort_field: property,
      sort:
        property === queries.sort_field ? queries.sort !== true : true,
    });
  };

  const onSearch = (value) => {
    setSearchKey(value);
    _loadData({
      ...queries,
      search_key: value,
      page_number: 1,
    });
  };

  const _loadData = async ({ page_number, page_size, search_key, sort, sort_field } = {}) => {
    const newQueries = {
      ...queries,
    };
    if (page_number) {
      newQueries.page_number = page_number;
    }
    if (page_size) {
      newQueries.page_size = page_size;
    }
    if (sort_field) {
      newQueries.sort_field = sort_field;
    }
    newQueries.search_key = search_key || "";
    newQueries.sort = !(!sort || false);
    setLoading(true);
    dispatch(role === SYSTEM_ROLE.COMPANY ? fetchJobPageForCompany({...newQueries}) : fetchListJob({...newQueries}))
      .then(({ payload }) => {
        setQueries(newQueries);
        setLoading(false);
        if (payload) {
          setTotalElements(payload.totalElements);
        }
      });
  };

  useEffect(() => {
    _loadData();
  }, []);

  useEffect(() => {
    if (clientList)
      setData(clientList);
      if (clientList.filter(item => item.isAllowDelete && item).length == 0) {
        setIsOnSelectedAll(true);
      } else {
        setIsOnSelectedAll(false);
      }
  }, [clientList])

  return (
    <div>
      <div>
        <AppButton
          label={t('button.create')}
          onClick={() => role === SYSTEM_ROLE.COMPANY ? history.push('/admin/jobs/create') : history.push('/jobs/create')}
          style={{
            marginRight: '1rem'
          }}
        />
        <AppButton
          label={t('button.delete')}
          buttonColor={AppColor.pink}
          onClick={() => setShowDeleteModal(true)}
          disabled={selected.length === 0}
        />
      </div>
      <Box my={3}>
        <InputSearch
          onSearch={onSearch}
          searchText={searchKey}
        />
      </Box>
      <AppTable
        onRequestSort={onRequestSort}
        order={queries.sort === true ? "asc" : "desc"}
        orderBy={queries.sort_field}
        rowCount={data.length}
        numSelected={selected.length}
        onSelectAllClick={_handleSelectAll}
        isOnSelectedAll={isOnSelectedAll}
        headCells={headCells.filter(item => {
          return isCoordinator || item.id !== 'participant_name'
        })}
      >
        <TableBody>
          {loading && (<TableRow>
            <TableCell colSpan={isCoordinator ? headCells.length + 1 : headCells.length} padding="none">
              <LinearProgress />
            </TableCell>
          </TableRow>)}
          {data.map((row, index) => (
            <TableRow key={index}>
              <TableCell padding="checkbox">
                {
                  row.isAllowDelete && (
                    <Checkbox
                      checked={selected.indexOf(row.id) !== -1}
                      inputProps={{ 'aria-labelledby': row.id }}
                      onClick={() => _handleSelect(row.id)}
                      color="primary"
                    />
                  )
                }
              </TableCell>
              <TableCell>{row.title}</TableCell>
              { isCoordinator && (
                  <TableCell>{row?.coordParticipant?.name}</TableCell>
              )}
              <TableCell>{isHospital ? row?.specialty?.name : row?.support?.name}</TableCell>
              <TableCell>{row.endDate}</TableCell>
              <TableCell>
                <Chip
                  style={{
                    backgroundColor: JOB_STATUS.ON_GOING === row.jobStatus
                      ? AppColor.orange
                      : JOB_STATUS.AVAILABLE === row.jobStatus
                        ? AppColor.green
                        : AppColor.blue,
                    color: AppColor.white
                  }}
                  size="small"
                  label={t(`jobStatus.${row.jobStatus}`)}
                />
              </TableCell>
              <TableCell>
                <Link
                  to={`/jobs/detail/${row.id}`}
                  className="text-center"
                >
                  <Button variant="text">
                    <i className="fas fa-edit" />
                  </Button>
                </Link>
              </TableCell>
            </TableRow>
          ))}

          {emptyRows > 0 && (
            <TableRow style={{ height: 53 }}>
              <TableCell colSpan={headCells.length} className="text-center">
                {t("label.no_data")}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              colSpan={headCells.length}
              count={totalElements}
              rowsPerPage={queries.page_size}
              page={queries.page_number - 1}
              SelectProps={{
                inputProps: { "aria-label": "rows per page" },
                native: true,
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
              labelRowsPerPage={`${t("label.rows_per_page")}:`}
              labelDisplayedRows={({ from, to, count }) =>
                t("label.from_to", {
                  from,
                  to,
                  count:
                    count !== -1 ? count : `${t("text.more_than")} ${to}`,
                })
              }
            />
          </TableRow>
        </TableFooter>
      </AppTable>
      <AppConfirmDialog
        open={showDeleteModal}
        onConfirm={() => _handleDeleteClients()}
        onClose={() => setShowDeleteModal(false)}
        title={t('title.delete_jobs')}
        content={t('text.delete_jobs')}
      />
    </div>
  );
};

export default JobTable;
